import type { ApiHolder, ApiRef } from '@backstage/core-plugin-api';

/**
 * Branded pairing of an {@link ApiRef} with its implementation. Produced
 * only via {@link apiEntry}, which is generic over `T` so the ref and the
 * implementation must agree on the same type parameter at the call site.
 *
 * This is what makes the `ScopedApiHolder` constructor type-sound: the
 * compiler refuses entries where the implementation does not match the
 * ref, so the runtime check in `get` can trust the brand.
 *
 * The implementation type is intentionally erased to `unknown` after
 * construction: ScopedApiHolder only needs `ref.id` + the value, and
 * making the brand generic would force a heterogeneous array of
 * `ApiEntry<T1> | ApiEntry<T2> | ...` on every callsite.
 */
export class ApiEntry {
  constructor(readonly ref: ApiRef<unknown>, readonly impl: unknown) {}
}

/**
 * Pair an {@link ApiRef} with a matching implementation. The generic
 * parameter is bound by the ref, so `apiEntry(techRadarApiRef, 42)` is
 * a compile error.
 *
 * @example
 *   new ScopedApiHolder(parent, [apiEntry(techRadarApiRef, catalogApi)])
 */
export function apiEntry<T>(ref: ApiRef<T>, impl: T): ApiEntry {
  return new ApiEntry(ref as ApiRef<unknown>, impl);
}

/**
 * Predicate that trusts the {@link ApiRef} as a brand: a value registered
 * under `ref.id` was supplied by the caller as the implementation of `ref`,
 * so we treat it as `T`. The runtime check is intentionally minimal — the
 * type-soundness contract is enforced by {@link apiEntry}, which the
 * constructor requires for every entry.
 */
function isImplOf<T>(_ref: ApiRef<T>, value: unknown): value is T {
  return value !== undefined;
}

/**
 * An {@link ApiHolder} that overlays a fixed set of API implementations on
 * top of a parent holder. Lookups for refs that have an override return the
 * override; all other refs delegate to the parent.
 */
export class ScopedApiHolder implements ApiHolder {
  private readonly overrides = new Map<string, unknown>();

  constructor(
    private readonly parent: ApiHolder,
    entries: ReadonlyArray<ApiEntry>,
  ) {
    for (const entry of entries) {
      this.overrides.set(entry.ref.id, entry.impl);
    }
  }

  get<T>(ref: ApiRef<T>): T | undefined {
    const override = this.overrides.get(ref.id);
    if (isImplOf(ref, override)) {
      return override;
    }
    return this.parent.get(ref);
  }
}
